import axios from "axios";
import { ordersApi, type SupportAttachmentUpload } from "./api";
import { AttachmentUploadError } from "./orderSupportRequestError";
import { sha256Hex } from "./orderSupportRequestPayload";

/**
 * Envio direto dos anexos ao S3: o arquivo não passa pelo API Gateway nem pelo proxy da EC2, que já recusaram e
 * estragaram uploads. A API assina uma URL por arquivo, presa ao pedido, ao envio e ao usuário; na criação ela lê o
 * arquivo do S3, confere, manda ao Zendesk e apaga.
 */

const RETRY_DELAYS_MS = [500, 1500];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Rede ou erro do S3 (5xx) tenta de novo; 4xx (URL vencida, arquivo fora da política) não adianta repetir. */
const isRetryableStorageError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status >= 500;
};

/**
 * POST de formulário ao S3 com a URL assinada. Axios puro, fora do ApiClient: o token do usuário nunca vai à AWS.
 * Até 3 tentativas; `onProgress` recebe os bytes já enviados do arquivo.
 */
export async function uploadToStorage(
  target: Pick<SupportAttachmentUpload, "url" | "fields">,
  file: File,
  onProgress?: (loadedBytes: number) => void
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    const formData = new FormData();
    for (const [name, value] of Object.entries(target.fields)) formData.append(name, value);
    // O S3 ignora os campos que vêm depois do arquivo: ele vai por último.
    formData.append("file", file, file.name);
    try {
      await axios.post(target.url, formData, {
        onUploadProgress: (event) => onProgress?.(event.loaded),
      });
      return;
    } catch (error) {
      if (attempt >= RETRY_DELAYS_MS.length || !isRetryableStorageError(error)) throw error;
      onProgress?.(0);
      await wait(RETRY_DELAYS_MS[attempt]);
    }
  }
}

export interface AttachmentUploadProgress {
  /** Arquivo em envio, a partir de 1. */
  current: number;
  total: number;
  /** Do arquivo atual, de 0 a 100. */
  percent: number;
}

interface ApiErrorLike {
  response?: { status?: number; data?: { message?: unknown; errors?: { field?: unknown; message?: unknown }[] } | null };
}

const messageOf = (value: unknown) => (typeof value === "string" && value.trim() ? value : null);

/** Recusa do pedido de URL: 400 é o arquivo (mensagem do back); rede, 503 e outros, "Tentar novamente". */
function toUploadUrlError(file: File, error: unknown): AttachmentUploadError {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status === 400) {
    const message =
      messageOf(response.data?.errors?.[0]?.message) ?? messageOf(response.data?.message) ?? `${file.name}: arquivo recusado.`;
    return new AttachmentUploadError(file.name, message, false);
  }
  const message =
    response?.status === 503
      ? messageOf(response.data?.message) ?? "O envio de anexos não está disponível agora. Tente novamente mais tarde."
      : `Não foi possível preparar o envio de ${file.name}. Verifique sua conexão e tente novamente.`;
  return new AttachmentUploadError(file.name, message, true);
}

interface UploadAttachmentsDirectInput {
  orderId: string;
  clientRequestId: string;
  files: File[];
  /** Chave de cada arquivo já enviado nesta sessão do formulário: "Tentar novamente" não sobe de novo. */
  uploadedKeys: Map<File, string>;
  onProgress?: (progress: AttachmentUploadProgress) => void;
}

/** Sobe ao S3, um por vez, os arquivos ainda sem chave e devolve as chaves na ordem dos arquivos. */
export async function uploadAttachmentsDirect({
  orderId,
  clientRequestId,
  files,
  uploadedKeys,
  onProgress,
}: UploadAttachmentsDirectInput): Promise<string[]> {
  const pending = files.filter((file) => !uploadedKeys.has(file));

  for (const [index, file] of pending.entries()) {
    const report = (loadedBytes: number) =>
      onProgress?.({
        current: index + 1,
        total: pending.length,
        percent: file.size > 0 ? Math.min(100, Math.round((loadedBytes / file.size) * 100)) : 100,
      });
    report(0);

    let target: SupportAttachmentUpload;
    try {
      const sha256 = await sha256Hex(file);
      target = await ordersApi.createAttachmentUploadUrl(orderId, {
        clientRequestId,
        fileName: file.name,
        size: file.size,
        sha256,
      });
    } catch (error) {
      throw toUploadUrlError(file, error);
    }

    try {
      await uploadToStorage(target, file, report);
    } catch {
      throw new AttachmentUploadError(
        file.name,
        `Não foi possível enviar ${file.name}. Verifique sua conexão e tente novamente.`,
        true
      );
    }
    uploadedKeys.set(file, target.key);
  }

  return files.map((file) => uploadedKeys.get(file) as string);
}

/**
 * Posições dos anexos recusados na criação (400 em "Attachments[n]": expirado, apagado, alterado ou de outro envio).
 * Com envio direto, esses arquivos são enviados de novo antes de uma segunda tentativa.
 */
export function rejectedAttachmentIndexes(error: unknown): number[] {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status !== 400 || !Array.isArray(response.data?.errors)) return [];
  return response.data.errors
    .map((entry) => (typeof entry?.field === "string" ? /^attachments\[(\d+)\]$/i.exec(entry.field) : null))
    .filter((match): match is RegExpExecArray => match !== null)
    .map((match) => Number(match[1]));
}
